import MuiMenu from '@mui/material/Menu';
import { PopoverOrigin } from '@mui/material/Popover';
import { MenuListProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  fullWidth: {
    width: '100%',
  },
}));

interface IProps {
  id?: string;
  /**The DOM element used to set the position of the menu. */
  anchorEl?: HTMLElement | null;
  /** If true, the menu is visible. */
  open: boolean;
  /**Callback fired when the component requests to be closed.
   * Signature:
   * function(event: object, reason: string) => void
   * event: The event source of the callback.
   * reason: Can be: "escapeKeyDown", "backdropClick", "tabKeyDown".
   */
  onClose?: (event: React.MouseEvent<HTMLElement>) => void;
  children?: React.ReactNode;
  /**
   * This is the point on the anchor where the popover's anchorEl will attach to.
   * This is not used when the anchorReference is 'anchorPosition'.
   * Options: vertical: [top, center, bottom]; horizontal: [left, center, right].
   */
  anchorOrigin?: PopoverOrigin;
  /**
   * This is the point on the popover which will attach to the anchor's origin.
   * Options: vertical: [top, center, bottom, x(px)]; horizontal: [left, center, right, x(px)].
   */

  transformOrigin?: PopoverOrigin;
  /**
   * This function is called in order to retrieve the content anchor element.
   * It's the opposite of the anchorEl prop.
   * The content anchor element should be an element inside the popover.
   * It's used to correctly scroll and set the position of the popover.
   * The positioning strategy tries to make the content anchor element just above the anchor element.
   *
   */
  defaultValue?: string | string[];
  keepMounted?: boolean;
  menuListProps?: Partial<MenuListProps>;
  disableTransition?: boolean;
  paperFullWidth?: boolean;
  listClassName?: string;
}

/**
 * En "Menu" visar ett antal alternativ för användaren på en tillfällig yta.
 * Den visas när användaren interagerar med en knapp eller annan kontroll.
 *
 * Se dokumentation för [menus](https://material.io/design/components/menus.html) inom material design.
 *
 * Bygger på MaterialUI [Menu](https://material-ui.com/components/menus/)
 */
const EDMenu: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { classes, cx } = useStyles();

  const {
    id,
    anchorEl,
    open,
    onClose,
    children,
    anchorOrigin = { horizontal: 'left', vertical: 'bottom' },
    transformOrigin = { horizontal: 'left', vertical: 'top' },
    defaultValue,
  } = props;

  return (
    <MuiMenu
      id={id}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      defaultValue={defaultValue}
      keepMounted={props.keepMounted}
      MenuListProps={props.menuListProps}
      transitionDuration={props.disableTransition ? 0 : undefined}
      classes={{
        list: props.listClassName,
        paper: cx({ [classes.fullWidth]: props.paperFullWidth }),
      }}
    >
      {children}
    </MuiMenu>
  );
};

export default EDMenu;
